import { useUserPermissions } from '../../apis/user-details';
import { useMemo } from 'react';
import { UserRole } from '@kotipizzagroup/kotipizza-users-api-client';

export const CampaignsPermissionsKey = 'campaigns';
export const LoyaltyPermissionsKey = 'loyalty';
export const OrdersPermissionsKey = 'orders';
export const ProductsPermissionsKey = 'products';
export const ShopsPermissionsKey = 'shops';
export const UsersPermissionsKey = 'users';

export const CAMPAIGNS_READ = `${CampaignsPermissionsKey}.read`;
export const CAMPAIGNS_WRITE = `${CampaignsPermissionsKey}.write`;
export const LOYALTY_READ = `${LoyaltyPermissionsKey}.read`;
export const LOYALTY_WRITE = `${LoyaltyPermissionsKey}.write`;
export const ORDERS_READ = `${OrdersPermissionsKey}.read`;
export const ORDERS_WRITE = `${OrdersPermissionsKey}.write`;
export const PRODUCTS_READ = `${ProductsPermissionsKey}.read`;
export const PRODUCTS_WRITE = `${ProductsPermissionsKey}.write`;
export const SHOPS_READ = `${ShopsPermissionsKey}.read`;
export const SHOPS_WRITE = `${ShopsPermissionsKey}.write`;
export const USERS_READ = `${UsersPermissionsKey}.read`;
export const USERS_WRITE = `${UsersPermissionsKey}.write`;

export const checkPermissions = (userPermissions: Array<string>, requiredPermissions: Array<string>): boolean => {
  if (!userPermissions) {
    return false;
  }

  return requiredPermissions.every((x) => userPermissions.includes(x));
};

export const usePermission = (requiredRoles: string[]): boolean => {
  const { roles: userPermissions } = useUserPermissions();

  return useMemo(() => checkPermissions(userPermissions, requiredRoles), [userPermissions, requiredRoles]);
};

export const hasAnyPermission = (userPermissions: string[] = [], requiredPermissions: string[]): boolean => {
  return requiredPermissions.some((p) => userPermissions.includes(p));
};

/**
 * Who may reach the order views: an admin holding `orders.read`, or a restaurant user holding the
 * RBAC `Order_CanView` role.
 *
 * This decides who may open the order views at all. Which orders they then contain is the orders
 * API's decision, taken from the bearer token — not this client's.
 */
export const useCanViewOrders = (): boolean => {
  const { roles } = useUserPermissions();

  return useMemo(() => hasAnyPermission(roles, [ORDERS_READ, UserRole.Order_CanView]), [roles]);
};

/** Must match the guard on the `/out-of-stock` routes. */
export const useCanViewOutOfStock = (): boolean => {
  const { roles } = useUserPermissions();

  return useMemo(() => hasAnyPermission(roles, [PRODUCTS_READ, UserRole.OutOfStock_CanView]), [roles]);
};

/**
 * Someone who runs restaurants rather than the chain — a franchisee — as opposed to an admin.
 *
 * The two halves of `useCanViewOrders`, told apart: `Order_CanView` is what lets this user into the
 * order views, and the absence of `orders.read` is what makes them not an admin. Used to shape the
 * views around how each reaches them, never to decide what either may see.
 */
export const useIsRestaurantUser = (): boolean => {
  const { roles } = useUserPermissions();

  return useMemo(
    () => !checkPermissions(roles, [ORDERS_READ]) && checkPermissions(roles, [UserRole.Order_CanView]),
    [roles]
  );
};
